import {
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  format,
  startOfISOWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
} from 'date-fns';

export type ChartGranularity =
  'week' | 'month' | 'quarter' | 'semester' | 'year';

export type ValuePoint = { key: string; label: string; value: number };

// Hard cap on rendered buckets so a pathological range (e.g. weekly over
// 50 years) cannot freeze the page.
const MAX_BUCKETS = 400;

function startOfSemester(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() < 6 ? 0 : 6, 1);
}

const FLOOR: Record<ChartGranularity, (d: Date) => Date> = {
  week: startOfISOWeek,
  month: startOfMonth,
  quarter: startOfQuarter,
  semester: startOfSemester,
  year: startOfYear,
};

const NEXT: Record<ChartGranularity, (d: Date) => Date> = {
  week: (d) => addWeeks(d, 1),
  month: (d) => addMonths(d, 1),
  quarter: (d) => addQuarters(d, 1),
  semester: (d) => addMonths(d, 6),
  year: (d) => addYears(d, 1),
};

function bucketLabel(
  d: Date,
  granularity: ChartGranularity,
  language: string,
): string {
  switch (granularity) {
    case 'week':
      return new Intl.DateTimeFormat(language, {
        day: 'numeric',
        month: 'short',
      }).format(d);
    case 'month':
      return new Intl.DateTimeFormat(language, {
        month: 'short',
        year: '2-digit',
      }).format(d);
    case 'quarter': {
      const q = Math.floor(d.getMonth() / 3) + 1;
      return `${language === 'fr' ? 'T' : 'Q'}${q} ${d.getFullYear()}`;
    }
    case 'semester':
      return `S${d.getMonth() < 6 ? 1 : 2} ${d.getFullYear()}`;
    case 'year':
      return String(d.getFullYear());
  }
}

/** One chart row: bucket identity plus one numeric entry per series dataKey. */
export type ChartRow = {
  key: string;
  label: string;
  [dataKey: string]: string | number;
};

export function buildValueSeries(
  points: { date: Date; value: number }[],
  granularity: ChartGranularity,
  opts: { from: Date; to: Date; cumulative: boolean; language: string },
): ValuePoint[] {
  const { from, to, cumulative, language } = opts;
  if (from > to) return [];

  const sums = new Map<number, number>();
  for (const p of points) {
    if (p.date < from || p.date > to) continue;
    const bucket = FLOOR[granularity](p.date).getTime();
    sums.set(bucket, (sums.get(bucket) ?? 0) + p.value);
  }

  const series: ValuePoint[] = [];
  let running = 0;
  let cursor = FLOOR[granularity](from);
  while (cursor <= to && series.length < MAX_BUCKETS) {
    const bucketSum = sums.get(cursor.getTime()) ?? 0;
    running += bucketSum;
    series.push({
      key: format(cursor, 'yyyy-MM-dd'),
      label: bucketLabel(cursor, granularity, language),
      value: cumulative ? running : bucketSum,
    });
    cursor = NEXT[granularity](cursor);
  }

  return series;
}

/**
 * Builds one aligned row array from several point sets (one per series).
 * Buckets are deterministic from from/to/granularity, so the per-series
 * results of buildValueSeries zip together by index with no holes.
 * Series keys must not be 'key' or 'label' (safe for ISO currency codes).
 */
export function buildMultiValueSeries(
  pointsBySeries: Record<string, { date: Date; value: number }[]>,
  granularity: ChartGranularity,
  opts: { from: Date; to: Date; cumulative: boolean; language: string },
): ChartRow[] {
  const keys = Object.keys(pointsBySeries);
  if (keys.length === 0) return [];

  const perSeries = keys.map(
    (k) => [k, buildValueSeries(pointsBySeries[k], granularity, opts)] as const,
  );

  return perSeries[0][1].map((bucket, i) => {
    const row: ChartRow = { key: bucket.key, label: bucket.label };
    for (const [k, series] of perSeries) {
      row[k] = series[i].value;
    }
    return row;
  });
}
